import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CircleCalculator = () => {
  const [radius, setRadius] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const value = e.target.value;
    setRadius(value);
    setError('');
  };

  const calculateCircle = () => {
    if (!radius) {
      setError('Please enter a radius');
      return;
    }

    if (isNaN(radius) || parseFloat(radius) <= 0) {
      setError('Please enter a valid positive number for the radius');
      return;
    }

    const numericRadius = parseFloat(radius);
    const area = Math.PI * Math.pow(numericRadius, 2);
    const circumference = 2 * Math.PI * numericRadius;

    const values = [numericRadius, area, circumference];
    const labels = ['Radius', 'Area', 'Circumference'];

    const chartData = {
      labels: labels,
      datasets: [
        {
          label: 'Circle Measurements',
          data: values,
          backgroundColor: [
            'rgba(255, 99, 132, 0.5)',
            'rgba(54, 162, 235, 0.5)',
            'rgba(75, 192, 192, 0.5)'
          ],
          borderColor: [
            'rgba(255, 99, 132, 1)',
            'rgba(54, 162, 235, 1)',
            'rgba(75, 192, 192, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    setResults({
      radius: numericRadius,
      area: area.toFixed(2),
      circumference: circumference.toFixed(2),
      chartData
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Circle Calculator</title>
          <meta name="description" content="Calculate the area and circumference of a circle based on the radius." />
          <meta name="keywords" content="circle calculator, calculate area, calculate circumference, geometry calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">⭕</div>
            <h1 className="text-3xl font-bold text-gray-800">Circle Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the area and circumference of a circle based on the radius.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Radius:</label>
              <input
                type="text"
                value={radius}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter radius"
              />
            </div>
          </div>

          <button
            onClick={calculateCircle}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Circle
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Radius</h3>
                  <p className="text-lg">{results.radius} units</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Area</h3>
                  <p className="text-lg">{results.area} square units</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Circumference</h3>
                  <p className="text-lg">{results.circumference} units</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Measurements'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Circle Properties'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Circle Measurements'
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Circle Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Radius:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the radius of the circle.</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The area of the circle.</li>
                  <li>The circumference of the circle.</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the calculated area and circumference.</li>
                  <li>View the visual representation of the circle properties.</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Ensure the radius is a positive number.</li>
                <li>Use this calculator for geometry problems, construction projects, or any scenario involving circle measurements.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation</h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a circle with a radius of 5 units:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Radius: 5 units</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Calculate the area using the formula: Area = π * radius²
                  <ul className="list-disc pl-6">
                    <li>Area = π * 5² ≈ 78.54 square units</li>
                  </ul>
                </li>
                <li>Calculate the circumference using the formula: Circumference = 2 * π * radius
                  <ul className="list-disc pl-6">
                    <li>Circumference = 2 * π * 5 ≈ 31.42 units</li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p>Here are some frequently asked questions about the Circle Calculator:</p>
            <div className="mt-4 space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What is the area of a circle?</strong>
                <p>The area of a circle is the space enclosed within the circle, calculated using the formula: Area = π * radius².</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: What is the circumference of a circle?</strong>
                <p>The circumference of a circle is the distance around the circle, calculated using the formula: Circumference = 2 * π * radius.</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg shadow-inner">
                <strong>Q: How do I use the calculator?</strong>
                <p>Enter the radius of the circle and click "Calculate Circle" to get the area and circumference.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CircleCalculator;